import React from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import { useParams } from "react-router-dom";
import TrainingPlanContent from "./components/TrainingPlanContent";
import PrintViewButton from "./PrintViewButton";
import { createTrainingPlanTheme } from "./theme/TrainingPlanTheme";
import { useExercisesBigQueryPublic } from "../../../hooks/useExercisesBigQuery";
import { useOrganizationDataByOrgId } from "../../../hooks/useOrganizationData";
import { useState, useEffect } from "react";

/**
 * TrainingPlanV3 - Public-facing training plan viewer for clients
 * This component loads a training plan by ID and displays it using Material-UI
 * No authentication required - designed for sharing training plans publicly
 */
const TrainingPlanV3 = () => {
  const { id: protocolId, organizationId } = useParams();

  // Fetch exercises from BigQuery for name resolution
  const { 
    data: exercisesData, 
    isLoading: exercisesLoading,
    isFetching: exercisesFetching 
  } = useExercisesBigQueryPublic({
    search: "",
    type: "all",
    limit: 1000,
    offset: 0,
    enabled: true,
    organization_id: organizationId,
  });

  // Fetch organization data for branding
  const organizationData = useOrganizationDataByOrgId({ organizationId });

  // Local state for training plan data
  const [planData, setPlanData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build exercises map for quick lookup - use persisted cache immediately, then update with fresh data
  const exercisesMap = React.useMemo(() => {
    const map = {};
    
    // First, load from persisted cache (available immediately on refresh)
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem("exerciseCache");
        if (cached) {
          const cacheData = JSON.parse(cached);
          const now = Date.now();
          const cacheExpiryTime = 30 * 60 * 1000; // 30 minutes
          
          Object.entries(cacheData).forEach(([exerciseId, cached]) => {
            const age = now - cached.timestamp;
            if (age < cacheExpiryTime && cached.exercise) {
              map[exerciseId] = cached.exercise;
            }
          });
        }
      } catch (error) {
        console.error("Error loading exercises from localStorage cache:", error);
      }
    }
    
    // Then, update with fresh data from React Query (when available)
    if (exercisesData?.data?.exercises) {
      exercisesData.data.exercises.forEach((exercise) => {
        map[exercise.id] = exercise;
      });
    }
    
    return map;
  }, [exercisesData]);

  const organizationName = organizationData?.name || "Organization";
  const brandingColors = organizationData?.brandingColors || null;

  // Auth token for API calls
  const authToken =
    localStorage.getItem("authToken") ||
    // eslint-disable-next-line no-undef
    (import.meta.env.VITE_API_KEY
      ? `Bearer ${import.meta.env.VITE_API_KEY}`
      : null);

  // Fetch training plan data
  useEffect(() => {
    const fetchProtocolData = async () => {
      try {
        setIsLoading(true);

        const localTest = false;
        // eslint-disable-next-line no-undef
        let baseURL = import.meta.env.VITE_API_BASE_URL;
        if (!baseURL || localTest) {
          // In development, fallback to localhost, but in production this should throw an error
          if (import.meta.env.VITE_ENV === "production" && !localTest) {
            throw new Error(
              "VITE_API_BASE_URL is not configured for production",
            );
          }
          baseURL = "https://app.mighty45.com/api/v1";
        }

        const url = `${baseURL}/organization/${organizationId}/protocol/${protocolId}?includeSplits=true&includeExercises=true&includeClient=true`;

        const headers = {
          "Content-Type": "application/json",
        };

        if (authToken) {
          headers.Authorization = authToken;
        }

        const response = await fetch(url, {
          method: "GET",
          headers,
        });

        if (!response.ok) {
          let errorMessage = "Unable to load training plan";

          switch (response.status) {
            case 403:
            case 404:
            case 401:
            case 500:
            default:
              try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorMessage;
              } catch {
                // Use default error message
              }
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();
        setPlanData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching protocol data:", error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    if (organizationId && protocolId) {
      fetchProtocolData();
    } else {
      setError("Missing required props: organizationId or protocolId");
      setIsLoading(false);
    }
  }, [organizationId, protocolId, authToken]);

  // Create custom theme with organization branding
  const trainingPlanTheme = createTrainingPlanTheme(brandingColors);

  // Show loading state - include exercises loading
  const loading = isLoading || exercisesLoading || !organizationId || !protocolId;

  if (loading) {
    return (
      <ThemeProvider theme={trainingPlanTheme}>
        <CssBaseline />
        <Container maxWidth="lg">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
            flexDirection="column"
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading your training plan...
            </Typography>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  // Show error state
  if (error) {
    return (
      <ThemeProvider theme={trainingPlanTheme}>
        <CssBaseline />
        <Container maxWidth="lg">
          <Box sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" component="div">
                Training Plan Unavailable
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              If you believe this is an error, please:
            </Typography>
            <ul>
              <li>Check that the link is correct</li>
              <li>Ensure the training plan is set to public</li>
              <li>Contact support if the problem persists</li>
            </ul>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  // Show no data state
  if (!planData) {
    return (
      <ThemeProvider theme={trainingPlanTheme}>
        <CssBaseline />
        <Container maxWidth="lg">
          <Box sx={{ py: 4 }}>
            <Alert severity="info">
              <Typography variant="h6" component="div">
                No Plan Data Available
              </Typography>
              <Typography variant="body2">
                Could not load the training plan. Please try again later.
              </Typography>
            </Alert>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {planData.name} - {organizationName}
        </title>
        <meta
          name="description"
          content={`A ${planData.difficulty} level plan for ${planData.type} fitness`}
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${planData.name} - ${organizationName}`}
        />
        <meta
          property="og:description"
          content={`A ${planData.difficulty} level plan for ${planData.type} fitness`}
        />
        <meta
          property="og:image"
          content={window.location.origin.toString() + "/PlateBlue10241024.png"}
        />

        {/* Apple-specific tags for iMessage */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        <meta name="apple-mobile-web-app-title" content={planData.name} />
        <link
          rel="apple-touch-icon"
          href={window.location.origin.toString() + "/PlateBlue10241024.png"}
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${planData.name} - ${organizationName}`}
        />
        <meta
          name="twitter:description"
          content={`A ${planData.difficulty} level plan for ${planData.type} fitness`}
        />
        <meta
          name="twitter:image"
          content={window.location.origin.toString() + "/PlateBlue10241024.png"}
        />
      </Helmet>

      {/* Add the print view button */}
      <PrintViewButton
        organizationId={organizationId}
        protocolId={protocolId}
      />

      <ThemeProvider theme={trainingPlanTheme}>
        <CssBaseline />
        <TrainingPlanContent
          planData={planData}
          exercisesMap={exercisesMap}
          organizationName={organizationName}
          brandingColors={brandingColors}
          organizationId={organizationId}
          protocolId={protocolId}
        />
      </ThemeProvider>
    </>
  );
};

export default TrainingPlanV3;
